// Reference Javascript Porter Stemmer. This code corresponds to the original
// 1980 paper available here: http://tartarus.org/martin/PorterStemmer/def.txt
// The latest version of this code is available at https://github.com/kristopolous/Porter-Stemmer
//
//  Porter, 1980, An algorithm for suffix stripping, Program, Vol. 14,
//  no. 3, pp 130-137,

const step2list: Record<string, string> = {
    ational: 'ate',
    tional: 'tion',
    enci: 'ence',
    anci: 'ance',
    izer: 'ize',
    bli: 'ble',
    alli: 'al',
    entli: 'ent',
    eli: 'e',
    ousli: 'ous',
    ization: 'ize',
    ation: 'ate',
    ator: 'ate',
    alism: 'al',
    iveness: 'ive',
    fulness: 'ful',
    ousness: 'ous',
    aliti: 'al',
    iviti: 'ive',
    biliti: 'ble',
    logi: 'log',
};

const step3list: Record<string, string> = {
    icate: 'ic',
    ative: '',
    alize: 'al',
    iciti: 'ic',
    ical: 'ic',
    ful: '',
    ness: '',
};

// consonant
const c = '[^aeiou]';
// vowel
const v = '[aeiouy]';
// consonant sequence
const C = `${c}[^aeiouy]*`;
// vowel sequence
const V = `${v}[aeiou]*`;
// [C]VC... is m>0
const mgr0 = `^(${C})?${V}${C}`;
// [C]VC[V] is m=1
const meq1 = `^(${C})?${V}${C}(${V})?$`;
// [C]VCVC... is m>1
const mgr1 = `^(${C})?${V}${C}${V}${C}`;
// vowel in stem
const s_v = `^(${C})?${v}`;

function step1(word: string): string {
    let w = word;

    // Step 1a
    const re1a = /^(.+?)(ss|i)es$/;
    const re1a2 = /^(.+?)([^s])s$/;
    if (re1a.test(w)) {
        w = w.replace(re1a, '$1$2');
    } else if (re1a2.test(w)) {
        w = w.replace(re1a2, '$1$2');
    }

    // Step 1b
    const reEed = /^(.+?)eed$/;
    const reEdIng = /^(.+?)(ed|ing)$/;
    if (reEed.test(w)) {
        const fp = reEed.exec(w)!;
        if (new RegExp(mgr0).test(fp[1])) w = w.replace(/.$/, '');
    } else if (reEdIng.test(w)) {
        const stem = reEdIng.exec(w)![1];
        if (new RegExp(s_v).test(stem)) {
            w = stem;
            if (/(at|bl|iz)$/.test(w)) {
                w = `${w}e`;
            } else if (/([^aeiouylsz])\1$/.test(w)) {
                w = w.replace(/.$/, '');
            } else if (new RegExp(`^${C}${v}[^aeiouwxy]$`).test(w)) {
                w = `${w}e`;
            }
        }
    }

    // Step 1c
    const re1c = new RegExp(`^(.*${v}.*)y$`);
    if (re1c.test(w)) w = `${re1c.exec(w)![1]}i`;

    return w;
}

function replaceSuffix(w: string, re: RegExp, list: Record<string, string>): string {
    if (!re.test(w)) return w;
    const [, stem, suffix] = re.exec(w)!;
    return new RegExp(mgr0).test(stem) ? stem + list[suffix] : w;
}

function step4(w: string): string {
    const re = /^(.+?)(al|ance|ence|er|ic|able|ible|ant|ement|ment|ent|ou|ism|ate|iti|ous|ive|ize)$/;
    const re2 = /^(.+?)(s|t)(ion)$/;
    if (re.test(w)) {
        const stem = re.exec(w)![1];
        return new RegExp(mgr1).test(stem) ? stem : w;
    }
    if (re2.test(w)) {
        const fp = re2.exec(w)!;
        const stem = fp[1] + fp[2];
        return new RegExp(mgr1).test(stem) ? stem : w;
    }
    return w;
}

function step5(word: string): string {
    let w = word;
    const re = /^(.+?)e$/;
    if (re.test(w)) {
        const stem = re.exec(w)![1];
        const isMgr1 = new RegExp(mgr1).test(stem);
        const isMeq1 = new RegExp(meq1).test(stem);
        const isCvc = new RegExp(`^${C}${v}[^aeiouwxy]$`).test(stem);
        if (isMgr1 || (isMeq1 && !isCvc)) w = stem;
    }

    if (/ll$/.test(w) && new RegExp(mgr1).test(w)) w = w.replace(/.$/, '');

    return w;
}

export function porterStemmer(word: string): string {
    if (word.length < 3) return word;

    const firstch = word.substring(0, 1);
    let w = firstch === 'y' ? firstch.toUpperCase() + word.substring(1) : word;

    w = step1(w);
    w = replaceSuffix(
        w,
        /^(.+?)(ational|tional|enci|anci|izer|bli|alli|entli|eli|ousli|ization|ation|ator|alism|iveness|fulness|ousness|aliti|iviti|biliti|logi)$/,
        step2list,
    );
    w = replaceSuffix(w, /^(.+?)(icate|ative|alize|iciti|ical|ful|ness)$/, step3list);
    w = step4(w);
    w = step5(w);

    // and turn initial Y back to y
    if (firstch === 'y') w = firstch.toLowerCase() + w.substring(1);

    return w;
}
